import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useGoalsWeight } from 'components/GoalsWeight/GoalsWeightContext';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector } from 'react-redux';
import { components } from 'react-select';
import { GoalsObjectiveGenericTreeViewTable } from 'screens/Team/Rabbit/Goals/AgGridTreeView/GoalsObjectiveGenericTreeViewTable';
import 'screens/Team/Rabbit/Goals/AgGridTreeView/styles/TreeView.css';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { checkGoalModuleEnabled } from 'utils/helpers/moduleAccess';

import { ChangeWeightage } from 'assets/assetsV1/iconComponents';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { getPersistenceKey } from 'constants/helper';

import { Button } from 'uiToolkitV1/Button';
import { Paper } from 'uiToolkitV1/Paper';
import { Select } from 'uiToolkitV1/Select';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import {
  goalsReviewCascadedViewReactQueryKey,
  useReviewPreviewPerformanceGoalsCascadedViewData,
} from './hooks/dataHooks';
import { getReviewGoalsTimelineList } from './services';

export const ReviewCycleOption = ({ children, ...props }) => (
  <components.Option {...props}>
    <TextBodyMedium bold color="text.g1" w="100%">
      {children}
    </TextBodyMedium>
  </components.Option>
);

const SelectHeader = ({
  goalsTimelineList = [],
  currentReviewCycleIndex,
  setCurrentReviewCycleIndex,
  employeeId,
  refetchCascadedViewData,
}) => {
  const { moduleName } = useOrganisationSettings();

  const { showSidePanel, hideSidePanel } = useGoalsWeight();
  const changeWeightageEnabled = goalsTimelineList?.[currentReviewCycleIndex]?.isOkrChangeWeightageAllowed;

  const reviewCycleOptions = goalsTimelineList.map(({ name }, index) => {
    if (name === 'Current Status') {
      return {
        value: index,
        label: `Currently active ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
      };
    }
    return {
      value: index,
      label: `${name}`,
    };
  });

  const reviewCycleChangeHandler = (selectedOption) => {
    setCurrentReviewCycleIndex(selectedOption.value);
  };

  const openMyWeightageSidePanel = () => {
    showSidePanel({
      employeeId,
      canEdit: true,
      preSelectedOptionId: goalsTimelineList?.[currentReviewCycleIndex]?.feedbackFormId,
      isPreSelectActiveGoals: goalsTimelineList?.[currentReviewCycleIndex]?.feedbackFormId === null,
      onCancel: () => {
        hideSidePanel();
      },
      onSuccess: () => {
        hideSidePanel();
        refetchCascadedViewData();
      },
    });
  };

  return (
    <Flex px={3} pb={3} justifyContent="space-between" alignItems="center">
      <Flex alignItems="center">
        <TextLabel>Review Cycle: </TextLabel>
        <div>
          <Select
            withBackground
            value={reviewCycleOptions?.[currentReviewCycleIndex]}
            options={reviewCycleOptions}
            handleChange={reviewCycleChangeHandler}
            components={{
              Option: ReviewCycleOption,
            }}
          />
        </div>
      </Flex>
      <Button onClick={openMyWeightageSidePanel} StartIcon={ChangeWeightage} isDisabled={!changeWeightageEnabled}>
        Manage {moduleName} weightage
      </Button>
    </Flex>
  );
};

const ReviewGoalsTable = () => {
  const gridRef = useRef();
  const [currentReviewCycleIndex, setCurrentReviewCycleIndex] = useState(null);
  const [, setLoadingReviewsList] = useState(false);
  const [goalsTimelineList, setGoalsTimelineList] = useState([]);

  const loggedInEmployeeData = useSelector(selectLoggedInEmployeeData);
  const { id: employeeId } = loggedInEmployeeData;

  const fetchGoalsTimelineList = useCallback(async () => {
    setLoadingReviewsList(true);
    try {
      const goalsTimelineListRes = await getReviewGoalsTimelineList({
        employeeId,
      });
      setGoalsTimelineList(goalsTimelineListRes);

      setCurrentReviewCycleIndex(goalsTimelineListRes.length - 1);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingReviewsList(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (employeeId) {
      fetchGoalsTimelineList();
    }
  }, [employeeId, fetchGoalsTimelineList]);

  const { data } = useReviewPreviewPerformanceGoalsCascadedViewData({
    startTS: goalsTimelineList?.[currentReviewCycleIndex]?.startTS,
    endTS: goalsTimelineList?.[currentReviewCycleIndex]?.endTS,
    employeeId,
    formId: goalsTimelineList?.[currentReviewCycleIndex]?.feedbackFormId,
  });

  const queryClient = useQueryClient();

  const refetchCascadedViewData = () => {
    queryClient.invalidateQueries(
      goalsReviewCascadedViewReactQueryKey({
        startTS: goalsTimelineList?.[currentReviewCycleIndex]?.startTS,
        endTS: goalsTimelineList?.[currentReviewCycleIndex]?.endTS,
        employeeId,
        formId: goalsTimelineList?.[currentReviewCycleIndex]?.feedbackFormId,
      })
    );
  };

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.REVIEW;
    const viewId = 'MyReviews';
    const tabId = 'PreviewPerformance';
    const uniqueId = 'PreviewPerfGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  if (!checkGoalModuleEnabled()) {
    return null;
  }

  return (
    <Box px={4} pb={4}>
      <Paper px={6} py={3}>
        <SelectHeader
          goalsTimelineList={goalsTimelineList}
          currentReviewCycleIndex={currentReviewCycleIndex}
          setCurrentReviewCycleIndex={setCurrentReviewCycleIndex}
          employeeId={employeeId}
          refetchCascadedViewData={refetchCascadedViewData}
        />
        <GoalsObjectiveGenericTreeViewTable
          gridRef={gridRef}
          gridStatePersistenceKey={gridStatePersistenceKey}
          data={data}
          refetchHandler={refetchCascadedViewData}
          showReviewWeightageColumn
          source="PreviewPerformance"
          isReview
        />
      </Paper>
    </Box>
  );
};

export { ReviewGoalsTable };
